import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  FormControl as MuiFormControl,
  Radio,
  Select,
  Skeleton,
  Slide,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import {
  createAddress,
  deleteAddress,
  getUserAddresses,
  updateAddress,
} from "../../../services/userAddressService";
import {
  CreateUserAddressRequest,
  UpdateUserAddressRequest,
  UserAddress,
} from "../../../types/userAddress";
import {
  getNationalLengthForCountry,
  inferCountryCodeFromPhone,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  stripDialCodeFromPhone,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import {
  alphaOnly,
  isAlphaText,
  validationMessages,
} from "../../../utils/InputValidation";
import CountryCodeSelect from "../../common/CountryCodeSelect";

// --- Types & Interfaces ---

interface AddressSelectorProps {
  selectedAddressId?: string;
  onAddressSelect: (addressId: string) => void;
  onAddressChange: (address: UserAddress) => void;
  showHeader?: boolean;
}

interface AddressFormData {
  addressLine1: string;
  city: string;
  country: string;
  phoneNumber: string;
  deliveryInstructions: string;
  isDefault: boolean;
}

type AddressFieldErrors = Partial<Record<keyof AddressFormData, string>>;

const DEFAULT_COUNTRY_CODE = "TJ";
const DEFAULT_COUNTRY_NAME = "Tajikistan";

const createEmptyFormData = (isDefault = false): AddressFormData => ({
  addressLine1: "",
  city: "",
  country: DEFAULT_COUNTRY_NAME,
  phoneNumber: "",
  deliveryInstructions: "",
  isDefault,
});

// --- Transition for Mobile Dialog ---
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// --- Memoized Address Card Component ---
const AddressCardItem = React.memo(
  ({
    address,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
  }: {
    address: UserAddress;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onEdit: (addr: UserAddress) => void;
    onDelete: (addr: UserAddress) => void;
  }) => {
    const { t: myTranslate } = useTranslation();
    const formattedAddress = useMemo(() => {
      return [
        address.addressLine1,
        address.city,
        address.country,
      ]
        .filter(Boolean)
        .join(", ");
    }, [address]);

    return (
      <Card
        elevation={isSelected ? 3 : 1}
        sx={{
          height: "100%",
          position: "relative",
          border: isSelected
            ? `2px solid ${amazonColors.orange}`
            : "1px solid #e0e0e0",
          backgroundColor: isSelected ? "rgba(255, 153, 0, 0.04)" : "white",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: amazonColors.orange,
            boxShadow: 2,
          },
        }}
      >
        <CardActionArea
          onClick={() => onSelect(address.id)}
          sx={{ height: "100%", alignItems: "flex-start" }}
        >
          <CardContent sx={{ pb: 6 }}>
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Radio
                checked={isSelected}
                value={address.id}
                sx={{
                  mt: -1,
                  ml: -1,
                  color: isSelected ? amazonColors.orange : "action.disabled",
                  "&.Mui-checked": { color: amazonColors.orange },
                }}
              />
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={0.5}
                  flexWrap="wrap"
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    lineHeight={1.2}
                  >
                    {address.city}
                  </Typography>
                  {address.isDefault && (
                    <Chip
                      icon={<StarIcon style={{ fontSize: 16 }} />}
                      label={myTranslate("address.default")}
                      size="small"
                      className="notranslate"
                      translate="no"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: amazonColors.yellow,
                        color: "#111",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>

                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ mb: 1, lineHeight: 1.5 }}
                >
                  {formattedAddress}
                </Typography>

                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  <span className="notranslate" translate="no">
                    {myTranslate("address.phone")}:
                  </span>{" "}
                  {address.phoneNumber}
                </Typography>

                {address.deliveryInstructions && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      fontStyle: "italic",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    "{address.deliveryInstructions}"
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>

        {/* Action Buttons */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            className="notranslate"
            translate="no"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(address);
            }}
            sx={{ color: amazonColors.blue, textTransform: "none" }}
          >
            {myTranslate("common.edit")}
          </Button>
          {!address.isDefault && (
            <Button
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              className="notranslate"
              translate="no"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(address);
              }}
              sx={{ color: "error.main", textTransform: "none" }}
            >
              {myTranslate("common.delete")}
            </Button>
          )}
        </Box>
      </Card>
    );
  },
);
AddressCardItem.displayName = "AddressCardItem";

// --- Main Component ---

const AddressSelector: React.FC<AddressSelectorProps> = ({
  selectedAddressId,
  onAddressSelect,
  onAddressChange,
  showHeader = true,
}) => {
  const { t: myTranslate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<UserAddress | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(
    null,
  );

  // Form State
  const [selectedCountryCode, setSelectedCountryCode] =
    useState<string>(DEFAULT_COUNTRY_CODE);

  const [formData, setFormData] = useState<AddressFormData>(
    createEmptyFormData(),
  );

  const [fieldErrors, setFieldErrors] = useState<AddressFieldErrors>({});

  // --- Loaders ---

  const loadAddresses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserAddresses();
      const userAddresses = response.data || [];
      setAddresses(userAddresses);

      if (!selectedAddressId && userAddresses.length > 0) {
        const defaultAddress =
          userAddresses.find((addr) => addr.isDefault) || userAddresses[0];
        if (defaultAddress) {
          onAddressSelect(defaultAddress.id);
          onAddressChange(defaultAddress);
        }
      }
    } catch (err) {
      setError(myTranslate("checkout.loadAddressesFailed"));
      console.error("Error loading addresses:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAddressId, onAddressSelect, onAddressChange]);

  useEffect(() => {
    loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handlers ---

  const handleAddressSelect = useCallback(
    (addressId: string) => {
      onAddressSelect(addressId);
      const selectedAddress = addresses.find((addr) => addr.id === addressId);
      if (selectedAddress) {
        onAddressChange(selectedAddress);
      }
    },
    [addresses, onAddressSelect, onAddressChange],
  );

  const handleAddAddress = () => {
    setEditingAddress(null);
    setFormData(createEmptyFormData(addresses.length === 0));
    setSelectedCountryCode(DEFAULT_COUNTRY_CODE);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleEditAddress = useCallback((address: UserAddress) => {
    const inferredCode =
      inferCountryCodeFromPhone(address.phoneNumber) ?? DEFAULT_COUNTRY_CODE;
    const nationalDigits = stripDialCodeFromPhone(
      address.phoneNumber,
      inferredCode,
    );

    setEditingAddress(address);
    setFormData({
      addressLine1: address.addressLine1.trim(),
      city: alphaOnly(address.city || ""),
      country: alphaOnly(address.country || DEFAULT_COUNTRY_NAME),
      phoneNumber: trimPhoneToNationalLength(
        inferredCode,
        nationalDigits || "",
      ),
      deliveryInstructions: address.deliveryInstructions?.trim() || "",
      isDefault: address.isDefault,
    });
    setSelectedCountryCode(inferredCode);
    setFieldErrors({});
    setDialogOpen(true);
  }, []);

  const handleDeleteAddress = useCallback((address: UserAddress) => {
    setPendingDelete(address);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialogOpen(false);
    setPendingDelete(null);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      setIsDeleting(true);
      await deleteAddress(pendingDelete.id);
      await loadAddresses();
      if (selectedAddressId === pendingDelete.id) {
        onAddressSelect("");
      }
    } catch (err) {
      setError(myTranslate("checkout.deleteAddressFailed"));
      console.error(err);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, loadAddresses, selectedAddressId, onAddressSelect]);

  const handleSaveAddress = async () => {
    if (isSaving) return;
    if (!validateForm()) return;

    const cleanedPhone = sanitizePhoneDigits(formData.phoneNumber);
    const e164Phone = toE164(selectedCountryCode, cleanedPhone);

    try {
      setIsSaving(true);
      const createData: CreateUserAddressRequest = {
        addressLine1: formData.addressLine1.trim(),
        city: formData.city.trim(),
        country: formData.country.trim(),
        phoneNumber: e164Phone,
        deliveryInstructions: formData.deliveryInstructions.trim(),
        isDefault: formData.isDefault,
        // Preserve legacy fields no longer shown in the form
        addressLine2: editingAddress?.addressLine2,
        region: editingAddress?.region,
        postalCode: editingAddress?.postalCode,
      };

      if (editingAddress) {
        const updateData: UpdateUserAddressRequest = {
          ...createData,
          isActive: editingAddress.isActive ?? true,
        };
        await updateAddress(editingAddress.id, updateData);
      } else {
        await createAddress(createData);
      }

      await loadAddresses();
      handleCloseDialog();
    } catch (err) {
      setError(myTranslate("checkout.saveAddressFailed"));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseDialog = () => {
    if (isSaving) return;
    setDialogOpen(false);
  };

  const handleDialogExited = () => {
    setEditingAddress(null);
    setFieldErrors({});
  };

  // --- Validation & Helpers ---

  const maxPhoneLength = getNationalLengthForCountry(selectedCountryCode);

  const updateField = (key: keyof AddressFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const errors: AddressFieldErrors = {};
    const { addressLine1, city, phoneNumber } = formData;
    const phoneDigits = sanitizePhoneDigits(phoneNumber);

    if (!addressLine1?.trim())
      errors.addressLine1 = validationMessages.required("Address Line 1");
    if (!city?.trim()) errors.city = validationMessages.required("City");
    else if (!isAlphaText(city))
      errors.city = validationMessages.alphaOnly("City");

    if (!phoneDigits) {
      errors.phoneNumber = validationMessages.phoneRequired;
    } else if (requiresStrictNationalLength(selectedCountryCode)) {
      const expected = getNationalLengthForCountry(selectedCountryCode);
      if (phoneDigits.length !== expected) {
        errors.phoneNumber = validationMessages.phoneLength(expected);
      }
    } else if (phoneDigits.length < 7) {
      errors.phoneNumber = validationMessages.phoneInvalid;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- Render ---

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Skeleton
              variant="rectangular"
              height={160}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box className="notranslate" translate="no">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        {showHeader && (
          <Typography
            variant="h6"
            fontWeight="700"
            className="notranslate"
            translate="no"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {myTranslate("address.deliveryAddress")}
          </Typography>
        )}
      </Box>

      {/* Address Grid */}
      <Grid container spacing={2}>
        {/* Address List */}
        {addresses.map((address) => (
          <Grid item xs={12} sm={6} key={address.id}>
            <AddressCardItem
              address={address}
              isSelected={selectedAddressId === address.id}
              onSelect={handleAddressSelect}
              onEdit={handleEditAddress}
              onDelete={handleDeleteAddress}
            />
          </Grid>
        ))}

        {/* "Add New" Card - Always visible at the end or empty state */}
        <Grid item xs={12} sm={addresses.length === 0 ? 12 : 6}>
          <Card
            variant="outlined"
            onClick={handleAddAddress}
            sx={{
              height: "100%",
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderStyle: "dashed",
              borderColor: "grey.400",
              cursor: "pointer",
              backgroundColor: "grey.50",
              "&:hover": {
                backgroundColor: "grey.100",
                borderColor: amazonColors.orange,
              },
            }}
          >
            <AddIcon sx={{ fontSize: 40, color: "grey.500", mb: 1 }} />
            <Typography
              variant="subtitle1"
              fontWeight="600"
              color="text.secondary"
              className="notranslate"
              translate="no"
            >
              {myTranslate("address.addNewAddress")}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        TransitionComponent={isMobile ? Transition : undefined}
        TransitionProps={{ onExited: handleDialogExited }}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 },
        }}
      >
        {isMobile ? (
          <AppBar
            sx={{
              position: "relative",
              bgcolor: amazonColors.blue,
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleCloseDialog}
                disabled={isSaving}
                aria-label="close"
                sx={{
                  color: "#fff",
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography
                sx={{ ml: 2, flex: 1, color: "#fff", fontWeight: 600 }}
                variant="h6"
                component="div"
                className="notranslate"
                translate="no"
              >
                {editingAddress
                  ? myTranslate("address.editYourAddress")
                  : myTranslate("address.addNewAddress")}
              </Typography>
              <Button
                onClick={handleSaveAddress}
                disabled={isSaving}
                className="notranslate"
                translate="no"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                }}
              >
                {isSaving
                  ? myTranslate("common.saving")
                  : myTranslate("common.save")}
              </Button>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle
            sx={{ bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}
            className="notranslate"
            translate="no"
          >
            {editingAddress
              ? myTranslate("address.editYourAddress")
              : myTranslate("address.addNewAddress")}
          </DialogTitle>
        )}

        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" noValidate sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Country Selection */}
              <Grid item xs={12}>
                <MuiFormControl fullWidth size="small" variant="outlined">
                  <InputLabel className="notranslate" translate="no">
                    {myTranslate("address.countryRegion")}
                  </InputLabel>
                  <Select
                    value={formData.country}
                    label={myTranslate("address.countryRegion")}
                    onChange={(e) => updateField("country", e.target.value)}
                    className="notranslate"
                    translate="no"
                    sx={{
                      "& .MuiSelect-select": {
                        bgcolor: "grey.50",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <MenuItem value="Tajikistan">Tajikistan</MenuItem>
                  </Select>
                </MuiFormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={myTranslate("address.streetAddress")}
                  placeholder={myTranslate("address.streetAddressPlaceholder")}
                  className="notranslate"
                  translate="no"
                  value={formData.addressLine1}
                  onChange={(e) => updateField("addressLine1", e.target.value)}
                  required
                  error={!!fieldErrors.addressLine1}
                  helperText={fieldErrors.addressLine1}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={myTranslate("address.city")}
                  className="notranslate"
                  translate="no"
                  value={formData.city}
                  onChange={(e) =>
                    updateField("city", alphaOnly(e.target.value))
                  }
                  required
                  error={!!fieldErrors.city}
                  helperText={fieldErrors.city}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={myTranslate("address.phoneNumber")}
                  className="notranslate"
                  translate="no"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    const trimmed = trimPhoneToNationalLength(
                      selectedCountryCode,
                      e.target.value || "",
                    );
                    updateField("phoneNumber", trimmed);
                  }}
                  required
                  error={!!fieldErrors.phoneNumber}
                  helperText={
                    fieldErrors.phoneNumber ||
                    myTranslate("address.phoneHelperText")
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CountryCodeSelect
                          value={selectedCountryCode}
                          onChange={(country) => {
                            const nextCode =
                              country?.code ?? DEFAULT_COUNTRY_CODE;
                            setSelectedCountryCode(nextCode);
                            setFormData((prev) => ({
                              ...prev,
                              phoneNumber: trimPhoneToNationalLength(
                                nextCode,
                                prev.phoneNumber || "",
                              ),
                            }));
                          }}
                          compact
                          selectSx={{ minWidth: 80 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: maxPhoneLength,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={myTranslate("address.deliveryInstructions")}
                  placeholder={myTranslate(
                    "address.deliveryInstructionsPlaceholder",
                  )}
                  className="notranslate"
                  translate="no"
                  value={formData.deliveryInstructions}
                  onChange={(e) =>
                    updateField("deliveryInstructions", e.target.value)
                  }
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.isDefault}
                      onClick={() =>
                        updateField("isDefault", !formData.isDefault)
                      }
                      sx={{
                        color: amazonColors.orange,
                        "&.Mui-checked": { color: amazonColors.orange },
                      }}
                    />
                  }
                  label={
                    <span className="notranslate" translate="no">
                      {myTranslate("address.makeDefaultAddress")}
                    </span>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {!isMobile && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseDialog}
              color="inherit"
              disabled={isSaving}
              className="notranslate"
              translate="no"
            >
              {myTranslate("common.cancel")}
            </Button>
            <Button
              onClick={handleSaveAddress}
              variant="contained"
              disableElevation
              disabled={isSaving}
              className="notranslate"
              translate="no"
              sx={{
                fontWeight: 800,
                bgcolor: amazonColors.orange,
                color: "#fff",
                "&:hover": { bgcolor: "#f0c14b" },
              }}
            >
              {isSaving
                ? myTranslate("common.saving")
                : editingAddress
                  ? myTranslate("address.saveChanges")
                  : myTranslate("address.addAddress")}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 4,
          sx: { borderRadius: 3 }, // More rounded corners for modern feel
        }}
      >
        <DialogTitle
          sx={{ pt: 3, pb: 1, fontWeight: 700 }}
          className="notranslate"
          translate="no"
        >
          {myTranslate("address.deleteAddress")}
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            className="notranslate"
            translate="no"
            sx={{ mb: 2 }}
          >
            {myTranslate("address.deleteConfirmation")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
            className="notranslate"
            translate="no"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "none",
              mr: 1,
            }}
          >
            {myTranslate("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disableElevation
            disabled={isDeleting}
            className="notranslate"
            translate="no"
            sx={{
              bgcolor: "error.main",
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              borderRadius: 2,
              "&:hover": {
                bgcolor: "error.dark",
              },
            }}
          >
            {isDeleting
              ? myTranslate("address.deleting")
              : myTranslate("address.yesDelete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressSelector;
